import { Style, animation } from './styles';

const { keys } = Object;

export type Brand = 'sas' | 'tas' | 'ias' | 'mc' | 'mktp' | 'travel360' | 'over60hols';

/**
 * Returns the base url for the given brand
 * @param {Brand} brand - The brand to get the base url for
 * @returns {string} - The base url for the given brand
 * @example urlByBrand('sas') // 'https://www.startsat60.com' 
**/
export const urlByBrand = (brand: Brand) => 
  brand == 'travel360' ? process.env.BASE_TRAVEL_URL : 
    brand == 'over60hols' ? process.env.BASE_OVER60HOLIDAYS_URL :
      process.env.BASE_WP_URL;

/**
 * Returns the site name for the given brand
 * @param {Brand} brand - The brand to get the site name for
 * @returns {string} - The site name for the given brand
 * @example siteNameByBrand('sas') // 'Starts at 60'
 */
export const siteNameByBrand = (brand: Brand) =>
  brand == 'travel360' ? 'Travel at 60' : 
    brand == 'over60hols' ? 'Over 60 Holidays' :
      'Starts at 60';

interface MediaQuery {
  up: string;
  down: string;
}

interface MediaSizes {
  xxs: number;
  xs: number;
  s: number;
  m: number;
  l: number;
  xl: number;
  xxl: number;
  xxxl: number;
}

type MediaSize = keyof MediaSizes;
type Media = {
  [K in MediaSize]: MediaQuery;
};

type MatchMedia = Record<MediaSize, string>;

interface BrandColors {
  primary: string;
  primary_dark: string;
  primary_light: string;
  secondary: string;
  accent: string;
  button: {
    background: string;
    color: string;
    hover_background: string;
    hover_color: string;
  };
}

interface Colors {
  bad: string;
  good: string;
  focus: string;
  red: string;
  white: string;
  light_grey: string;
  grey: string;
  dark_grey: string;
  black: string;
  transparent: string;
  sas: BrandColors;
  mc: BrandColors;
  mktp: BrandColors;
  ias: BrandColors;
  tas: BrandColors;
  travel360: BrandColors;
  over60hols: BrandColors;
  primary_light: string;
}

type BrandColor = keyof BrandColors;
export type Color = BrandColor | keyof Colors;

interface FontSizes {
  xxs: string;
  xs: string;
  s: string;
  m: string;
  l: string;
  xl: string;
  xxl: string;
  xxxl: string;
  xxxxl: string;
}

interface FontSpacings {
  m: string;
}

interface FontFamilies {
  primary: string;
  secondary: string;
  copy: string;
}

interface FontWeights {
  normal: number;
  bold: number;
}

export type FontSize = keyof FontSizes;
export type FontWeight = keyof FontWeights;
export type FontFamily = keyof FontFamilies;

interface Font extends FontSizes, FontWeights {
  families: FontFamilies;
  heights: FontSizes;
  spacings: FontSpacings;
}

interface Corners {
  s: string | number;
  m: string | number;
  l: string | number;
  xl: string | number;
  f: string | number;
}

export type Corner = keyof Corners;

type Logos = Record<
  Brand,
  {
    primary: string;
    secondary: string;
  }
>;

interface Layouts {
  column: Style;
  spacedColumn: (space: Size) => Style;
  row: Style;
  spacedRow: (space: Size) => Style;
  center: Style;
  container: (container: Container) => Style;
  filled: Style;
  panel: Style;
  contentPane?: number;
  sidebar?: number;
  wideContentPane?: number;
}

interface Layers {
  lowest: number;
  lower: number;
  low: number;
  mid: number;
  high: number;
  higher: number;
  highest: number;
}

interface Shadows {
  lowest: string;
  low: string;
  mid: string;
  high: string;
  highest: string;
}

interface Containers {
  s: number;
  m: number;
  l: number;
  xl: number;
  sidebar: number;
}

type Container = keyof Containers;

interface ScaleRaw {
  xxxxs?: number;
  xxxs?: number;
  xxs: number;
  xs: number;
  s: number;
  m: number;
  l: number;
  xl: number;
  xxl: number;
  xxxl?: number;
  xxxxl?: number;
}

export type Scale = Record<keyof ScaleRaw, string>;
export type Size = keyof Scale;

export interface ThemeV2 {
  colors: Colors;
  logos: Logos;
  corners: Corners;
  font: Font;
  inputReset: Style;
  layouts: Layouts;
  layers: Layers;
  scale: Scale;
  scaleRaw: ScaleRaw;
  shadows: Shadows;
  animations: {
    [key: string]: string;
  };
  mediaSizes: MediaSizes;
  matchMedia: MatchMedia;
  media: Media;
  layoutWidths: {
    wideContentPane: number,
    contentPane: number,
    sidebar: number,
  },
}

const rem = (px: number): string => `${px / 16}rem`;
const mediaQuery = (px: number): MediaQuery => ({
  up: `@media screen and (min-width: ${px}px)`,
  down: `@media screen and (max-width: ${px - 1}px)`,
});

const matchMediaQuery = (px: number): string => `(min-width: ${px}px)`;

const sasColors: BrandColors = {
  primary: '#651fff',
  primary_dark: '#5a1be4',
  primary_light: '#651fff',
  secondary: '#20ffc0',
  accent: '#ffb0e6',
  button: {
    background: '#20ffc0',
    color: '#000000',
    hover_background: '#000000',
    hover_color: '#ffffff',
  },
};

const colors: Colors = {
  red: '#ff0000',
  white: '#ffffff',
  light_grey: '#f4f5f6',
  grey: '#c3c4c6',
  dark_grey: '#6d6f73',
  black: '#263238',
  focus: '#3b99fc',
  bad: '#ff4d4d',
  good: '#00c06c',
  primary_light: '#7130FF',
  transparent: 'transparent',
  sas: sasColors,
  mc: sasColors,
  mktp: sasColors,
  ias: {
    primary: '#651fff',
    primary_dark: '#5a1be4',
    primary_light: '#651fff',
    secondary: '#ffb0e6',
    accent: '#fffd90',
    button: {
      background: '#651fff',
      color: '#ffffff',
      hover_background: '#5a1be4',
      hover_color: '#ffffff',
    },
  },
  tas: {
    primary: '#651fff',
    primary_dark: '#5a1be4',
    primary_light: '#651fff',
    secondary: '#fffd90',
    accent: '#ffb0e6',
    button: {
      background: '#20ffc0',
      color: '#000000',
      hover_background: '#000000',
      hover_color: '#ffffff',
    },
  },
  travel360: {
    primary: '#651FFF',
    primary_dark: '#4700E0',
    primary_light: '#F7F3FF',
    secondary: '#70ffd7',
    accent: '#ffb0e6',
    button: {
      background: '#20ffc0',
      color: '#000000',
      hover_background: '#000000',
      hover_color: '#ffffff',
    },
  },
  over60hols: {
    primary: '#651FFF',
    primary_dark: '#4700E0',
    primary_light: '#F7F3FF',
    secondary: '#70ffd7',
    accent: '#ffb0e6',
    button: {
      background: '#20ffc0',
      color: '#000000',
      hover_background: '#000000',
      hover_color: '#ffffff',
    },    
  },
};

const containers: Containers = {
  s: 320,
  sidebar: 340, // Sidebar width
  m: 660,
  l: 1160,
  xl: 1600, // Wide content pane
};

const logos = {
  sas: {
    primary: 'https://images.startsat60.com/frontend/logo/sasPrimary.svg',
    secondary: 'https://images.startsat60.com/frontend/logo/sasSecondary.svg',
    white: 'https://images.startsat60.com/frontend/logo/sasWhite.svg',
  },

  tas: {
    primary: 'https://images.startsat60.com/frontend/logo/tasPrimary.svg',
    secondary: 'https://images.startsat60.com/frontend/logo/tasSecondary.svg',
    white: 'https://images.startsat60.com/frontend/logo/tasWhite.svg',
  },

  ias: {
    primary: 'https://images.startsat60.com/frontend/logo/iasPrimary.svg',
    secondary: 'https://images.startsat60.com/frontend/logo/iasSecondary.svg',
    white: '',
  },

  mc: {
    primary: 'https://images.startsat60.com/frontend/logo/mcPrimary.svg',
    secondary: 'https://images.startsat60.com/frontend/logo/mcSecondary.svg',
    white: '',
  },
  mktp: {
    primary: 'https://images.startsat60.com/frontend/logo/sassyMarketplace.png',
    secondary:
      'https://images.startsat60.com/frontend/logo/sassyMarketplace.png',
    white: '',
  },
  travel360: {
    primary: 'https://images.startsat60.com/frontend/logo/tasPrimary.svg',
    secondary: 'https://images.startsat60.com/frontend/logo/tasSecondary.svg',
    white: 'https://images.startsat60.com/frontend/logo/tasWhite.svg',
  },
  over60hols: {
    primary: 'https://images.startsat60.com/frontend/logo/tasPrimary.svg',
    secondary: 'https://images.startsat60.com/frontend/logo/tasSecondary.svg',
    white: 'https://images.startsat60.com/frontend/logo/tasWhite.svg',
  },
};

const mediaSizes = {
  xxs: 0, // Small phones
  xs: 375, // Phones (portrait)
  s: 576, // Phones (landscape)
  m: 768, // Tablets
  l: 992, // Desktops
  xl: 1160, // Large desktops
  xxl: 1200, // Extra large desktops
  xxxl: 1600, // 4K desktops
};

const media = keys(mediaSizes).reduce(
  (result, key) => ({
    ...result,

    [key]: mediaQuery(mediaSizes[key]),
  }),
  {} as Media
);

const scaleRaw = {
  xxxxs: 3,
  xxxs: 5,
  xxs: 8,
  xs: 13,
  s: 21,
  m: 34,
  l: 55,
  xl: 89,
  xxl: 144,
  xxxl: 233,
  xxxxl: 377,
};

const theme: ThemeV2 = {
  colors,
  logos,
  font: {
    xxs: rem(10),
    xs: rem(12),
    s: rem(14),
    m: rem(16),
    l: rem(18),
    xl: rem(21),
    xxl: rem(28),
    xxxl: rem(40),
    xxxxl: rem(60),
    normal: 400,
    bold: 700,
    families: {
      primary: "'Poppins', Arial",
      secondary: "'Roboto', Arial",
      copy: 'Poppins, serif',
    },
    heights: {
      xxs: rem(16),
      xs: rem(18),
      s: rem(20),
      m: rem(22),
      l: rem(24),
      xl: rem(28),
      xxl: rem(36),
      xxxl: rem(48),
      xxxxl: rem(68),
    },
    spacings: {
      m: rem(0.1),
    },
  },
  inputReset: {
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    letterSpacing: 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    color: 'inherit',
    backgroundColor: 'transparent',
    border: 'none',
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  corners: {
    s: '3px',
    m: '5px',
    l: '12px',
    xl: '27.5px',
    f: '100vh',
  },
  animations: {
    fadeIn: animation({
      '0%': {
        opacity: 0,
        animationTimingFunction: 'ease-out',
      },
      '100%': {
        opacity: 1,
      },
    }),
    fadeOut: animation({
      '0%': {
        opacity: 1,
        animationTimingFunction: 'ease-out',
      },
      '100%': {
        opacity: 0,
      },
    }),
    slideIn: animation({
      '0%': {
        transform: 'translateX(-100%)',
      },
      '100%': {
        transform: 'translateX(0)',
      },
    }),
    slideInReverse: animation({
      '0%': {
        transform: 'translateX(100%)',
      },
      '100%': {
        transform: 'translateX(0)',
      },
    }),
    slideDown: animation({
      '0%': {
        maxHeight: 1,
      },
      '100%': {
        maxHeight: 999999,
      },
    }),
    spin: animation({
      '0%': {
        transform: 'rotate(0)',
      },
      '100%': {
        transform: 'rotate(359deg)',
      },
    }),
    lazy: animation({
      '0%': {
        backgroundPosition: '0% 92%',
      },
      '100%': {
        backgroundPosition: '100% 9%',
      },
    }),
    kenBurns: animation({
      '0%': {
        transformOrigin: 'left center',
        transform: 'scale(1.001)',
      },
      '50%': {
        transformOrigin: 'left center',
        transform: 'scale(1.25) translateX(-15%)',
      },
      '100%': {
        transformOrigin: 'left center',
        transform: 'scale(1.001)',
      },
    }),
    grow: animation({
      '0%': {
        transformOrigin: 'top',
        transform: 'scale(0)',
        maxHeight: 1,
      },
      '100%': {
        transformOrigin: 'top',
        transform: 'scale(1)',
        maxHeight: 350,
      },
    }),
  },
  layouts: {
    column: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    },
    spacedColumn: (space: Size) => ({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',

      '& > *': {
        marginTop: scaleRaw[space],
        marginLeft: 0,
      },

      '& > *:first-child': {
        marginTop: 0,
      },
    }),
    row: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    spacedRow: (space: Size) => ({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',

      '& > *': {
        marginLeft: scaleRaw[space],
        marginTop: 0,
      },

      '& > *:first-child': {
        marginLeft: 0,
      },
    }),
    container: (container: Container) => ({
      width: '100%',
      maxWidth: containers[container],
    }),
    filled: {
      '& > *:nth-child(n)': {
        flex: '1 0',
      },
    },
    panel: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: `${scaleRaw.s}px ${scaleRaw.xs}px`,

      [media.s.up]: {
        padding: `${scaleRaw.m}px ${scaleRaw.s}px`,
      },
    },
    center: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  shadows: {
    lowest:
      '0 5px 5px 0 rgba(38, 50, 56, 0.08), 0 8px 8px 0 rgba(38, 50, 56, 0.08)',
    low: '0 8px 8px 0 rgba(38, 50, 56, 0.08), 0 13px 13px 0 rgba(38, 50, 56, 0.08)',
    mid: '0 13px 13px 0 rgba(38, 50, 56, 0.08), 0 21px 21px 0 rgba(38, 50, 56, 0.08)',
    high: '0 21px 21px 0 rgba(38, 50, 56, 0.08), 0 34px 34px 0 rgba(38, 50, 56, 0.08)',
    highest:
      '0 34px 34px 0 rgba(38, 50, 56, 0.08), 0 55px 55px 0 rgba(38, 50, 56, 0.08)',
  },
  layers: {
    lowest: -999999,
    lower: -1000,
    low: -3,
    mid: 0,
    high: 3,
    higher: 1000,
    highest: 999999,
  },
  scaleRaw,
  scale: keys(scaleRaw).reduce<Scale>(
    (result, key) => ({
      ...result,

      [key]: `${scaleRaw[key]}px`,
    }),
    {} as any
  ),
  mediaSizes,
  media,
  matchMedia: keys(mediaSizes).reduce(
    (result, key) => ({
      ...result,

      [key]: matchMediaQuery(mediaSizes[key]),
    }),
    {} as MatchMedia
  ),
  layoutWidths: {
    wideContentPane: containers.xl,
    contentPane: containers.l,
    sidebar: containers.sidebar,
  }
};

export default theme;
